if (!customElements.get('quick-add-bulk')) {
  customElements.define(
    'quick-add-bulk',
    class QuickAddBulk extends BulkAdd {
      constructor() {
        super();
        this.quantity = this.querySelector('quantity-input');

        const debouncedOnChange = debounce((event) => {
          if (parseInt(event.target.value) === 0) {
            this.startQueue(event.target.dataset.index, parseInt(event.target.value));
          } else {
            this.validateQuantity(event);
          }
        }, ON_CHANGE_DEBOUNCE_TIMER);

        this.addEventListener('change', debouncedOnChange.bind(this));
        this.listenForActiveInput();
        this.listenForKeydown();
        this.lastActiveInputId = null;
        const pageParams = new URLSearchParams(window.location.search);
        window.pageNumber = decodeURIComponent(pageParams.get('page') || '');
      }

      connectedCallback() {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (
            event.source === 'quick-add' ||
            (event.cartData.items && !event.cartData.items.some((item) => item.id === parseInt(this.dataset.index))) ||
            (event.cartData.variant_id && !(event.cartData.variant_id === parseInt(this.dataset.index)))
          ) {
            return;
          }
          // If its another section that made the update
          this.onCartUpdate().then(() => {
            this.listenForActiveInput();
            this.listenForKeydown();
          });
        });
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
      }

      getInput() {
        return this.querySelector('quantity-input input');
      }

      selectProgressBar() {
        return this.querySelector('.progress-bar-container');
      }

      listenForActiveInput() {
        if (!this.classList.contains('hidden')) {
          this.getInput().addEventListener('focusin', (event) => event.target.select());
        }
        this.isEnterPressed = false;
      }

      listenForKeydown() {
        this.getInput().addEventListener('keydown', (event) => {
          if (event.key === 'Enter') {
            this.getInput().blur();
            this.isEnterPressed = true;
          }
        });
      }

      cleanErrorMessageOnType(event) {
        event.target.addEventListener(
          'keypress',
          () => {
            event.target.setCustomValidity('');
          },
          { once: true }
        );
      }

      onCartUpdate() {
        return new Promise((resolve, reject) => {
          fetch(`${this.getSectionsUrl()}?section_id=${this.closest('.collection').dataset.id}`)
            .then((response) => response.text())
            .then((responseText) => {
              const html = new DOMParser().parseFromString(responseText, 'text/html');
              const sourceQty = html.querySelector(
                `#quick-add-bulk-${this.dataset.id}-${this.closest('.collection').dataset.id}`
              );
              if (sourceQty) {
                this.innerHTML = sourceQty.innerHTML;
              }
              resolve();
            })
            .catch((e) => {
              console.error(e);
              reject(e);
            });
        });
      }

         updateMultipleQty(items) {
        this.selectProgressBar().classList.remove('hidden');
        const ids = Object.keys(items);
        const collectionIds = this.dataset.collectionIds || '';
        const productType = this.dataset.productType || '';
        const hasProperties = collectionIds || productType;

        const sections = this.getSectionsToRender().map((section) => section.section);
        const sectionsUrl = this.getSectionsUrl();

        const renderAndPublish = (parsedState) => {
          this.renderSections(parsedState, ids);
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'quick-add', cartData: parsedState });
        };

        const runUpdate = (updates) =>
          fetch(routes.cart_update_url, {
            ...fetchConfig(),
            body: JSON.stringify({
              updates: updates || items,
              attributes: { channel: 'online' },
              sections,
              sections_url: sectionsUrl,
            }),
          })
            .then((r) => r.text())
            .then((state) => JSON.parse(state))
            .then(renderAndPublish);

        if (!hasProperties) {
          runUpdate()
            .catch(() => {})
            .finally(() => {
              this.selectProgressBar().classList.add('hidden');
              this.requestStarted = false;
            });
          return;
        }

        fetch(`${routes.cart_url}.js`)
          .then((r) => r.json())
          .then((cart) => {
            const addItems = [];
            const updateItems = {};
            const inCartByVariant = {};
            (cart.items || []).forEach((item) => {
              inCartByVariant[item.variant_id] = (inCartByVariant[item.variant_id] || 0) + item.quantity;
            });

            ids.forEach((variantId) => {
              const qty = parseInt(items[variantId], 10);
              const variantIdNum = parseInt(variantId, 10);
              const inCart = (inCartByVariant[variantIdNum] || 0) > 0;

              if (qty > 0 && !inCart) {
                const item = { id: variantIdNum, quantity: qty, properties: {} };
                if (collectionIds) item.properties._collections = collectionIds;
                if (productType) item.properties._type = productType;
                addItems.push(item);
              } else {
                updateItems[variantId] = qty;
              }
            });

            const promises = [];
            if (addItems.length > 0) {
              promises.push(
                fetch(routes.cart_add_url, {
                  ...fetchConfig(),
                  body: JSON.stringify({ items: addItems, sections, sections_url: sectionsUrl }),
                })
                  .then((r) => r.text())
                  .then((state) => JSON.parse(state))
                  .then(renderAndPublish)
              );
            }
            if (Object.keys(updateItems).length > 0) {
              promises.push(runUpdate(updateItems));
            }

            return promises.length > 0 ? Promise.all(promises) : runUpdate();
          })
          .catch(() => {})
          .finally(() => {
            this.selectProgressBar().classList.add('hidden');
            this.requestStarted = false;
          });
      }

      getSectionsToRender() {
        return [
          {
            id: `quick-add-bulk-${this.dataset.id}-${this.closest('.collection-quick-add-bulk').dataset.id}`,
            section: this.closest('.collection-quick-add-bulk').dataset.id,
            selector: `#quick-add-bulk-${this.dataset.id}-${this.closest('.collection-quick-add-bulk').dataset.id}`,
          },
          {
            id: 'cart-icon-bubble',
            section: 'cart-icon-bubble',
            selector: '.shopify-section',
          },
          {
            id: 'CartDrawer',
            selector: '#CartDrawer',
            section: 'cart-drawer',
          },
        ];
      }

      renderSections(parsedState, ids) {
        const intersection = this.queue.filter((element) => ids.includes(element.id));
        if (intersection.length !== 0) return;
        this.getSectionsToRender().forEach((section) => {
          const sectionElement = document.getElementById(section.id);
          if (
            sectionElement &&
            sectionElement.parentElement &&
            sectionElement.parentElement.classList.contains('drawer')
          ) {
            parsedState.items.length > 0
              ? sectionElement.parentElement.classList.remove('is-empty')
              : sectionElement.parentElement.classList.add('is-empty');

            setTimeout(() => {
              document.querySelector('#CartDrawer-Overlay').addEventListener('click', this.cart.close.bind(this.cart));
            });
          }
          const elementToReplace =
            sectionElement && sectionElement.querySelector(section.selector)
              ? sectionElement.querySelector(section.selector)
              : sectionElement;
          if (elementToReplace) {
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          }
        });

        if (this.isEnterPressed) {
          this.querySelector(`#Quantity-${this.lastActiveInputId}`).select();
        }

        this.listenForActiveInput();
        this.listenForKeydown();
      }
    }
  );
}
